import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  clampCropResizeToCoveredSize,
  clampOrientedResizeToMaxSize,
} from '@/lib/transforms/single-object-oriented-resize'
import { isCornerResizeHandle } from '@/lib/transforms/unified-selection-oriented-resize'
import {
  getCurrentPhotoFillCoveredSize,
  getMaxPhotoFillSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import { computePhotoFillOffsetAfterResize } from '@/lib/photos/photo-fill-resize-offset'
import type { ResizeHandle } from '../types/interaction-state'

/**
 * Photo-filled shape: cap the resize at the photo's resolution, keep a non-corner (crop) resize
 * inside the covered image, and re-derive the photo's offset/scale for the new frame.
 */
export function applyPhotoFillResizeClamp(
  initial: ShapeData,
  dir: ResizeHandle,
  nextIn: { x: number; y: number; width: number; height: number },
  useAspectRatio: boolean
): {
  next: { x: number; y: number; width: number; height: number }
  photoFillUpdate: Pick<ShapeData, 'photoFill'> | undefined
} {
  let next = nextIn
  let photoFillUpdate: Pick<ShapeData, 'photoFill'> | undefined

  if (initial.photoFill) {
    const isCornerPhotoHandle = isCornerResizeHandle(dir)
    // Checkpoint load never carries true naturalWidth/Height on the wire, and the async
    // re-measure effect that backfills them can lose the race with a resize started
    // moments after a spread loads (or never fire at all if the preview URL wasn't
    // resolved yet when it ran) — fall back to persistedRenderedWidth/Height, which
    // shares the exact same aspect ratio, so the DPI ceiling, growth cap, and
    // scale-freeze math all still work correctly without it.
    const { naturalWidth: effectiveNaturalWidth, naturalHeight: effectiveNaturalHeight } =
      resolveEffectivePhotoFillNaturalSize(initial.photoFill)
    const maxSize = getMaxPhotoFillSize(effectiveNaturalWidth, effectiveNaturalHeight)

    next = clampOrientedResizeToMaxSize({
      initial,
      direction: dir,
      next,
      maxWidth: maxSize?.maxWidth,
      maxHeight: maxSize?.maxHeight,
      aspectLocked: useAspectRatio,
    })

    if (!isCornerPhotoHandle) {
      const coveredSize = getCurrentPhotoFillCoveredSize(
        initial.width,
        initial.height,
        effectiveNaturalWidth,
        effectiveNaturalHeight,
        initial.photoFill.scale
      )

      next = clampCropResizeToCoveredSize({
        initial,
        direction: dir,
        next,
        coveredWidth: coveredSize?.coveredWidth,
        coveredHeight: coveredSize?.coveredHeight,
      })
    }

    // Recompute photoFill every live frame against the TRUE current `next` — the exact
    // same formula (and the exact same inputs, once the gesture settles) that commit
    // uses. This was previously computed against a `next` that kept the gesture-start
    // width/height frozen (to stop the photo from visibly rescaling mid-drag), with the
    // render pinned to that same frozen size. That worked frame-to-frame, but the
    // browser's native cover-fit baseline (where `xMidYMid` centers the covered image)
    // depends on the frame's OWN size — freezing that size during the drag but then
    // switching to the true final size at commit is two different reference frames,
    // which produces a real, provable jump at release (verified numerically), not just
    // a perceived one. Using the true `next` throughout — live and commit alike —
    // means the last live frame and the commit are computed identically, so there is
    // nothing to jump between.
    const { offsetX, offsetY, scale } = computePhotoFillOffsetAfterResize({
      isCornerHandle: isCornerPhotoHandle,
      initial,
      next,
      offsetX: initial.photoFill.offsetX,
      offsetY: initial.photoFill.offsetY,
      scale: initial.photoFill.scale,
      naturalWidth: effectiveNaturalWidth,
      naturalHeight: effectiveNaturalHeight,
    })

    photoFillUpdate = {
      photoFill: { ...initial.photoFill, offsetX, offsetY, scale },
    }
  }

  return { next, photoFillUpdate }
}
